import React, {
  forwardRef, useEffect, useImperativeHandle, useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useWatch } from 'react-hook-form'
import { ConsignmentItemAdditionalSupplyChainActors } from 'features/declaration/form/components/AdditionalSupplyChainActors'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import SupportingDocuments from 'features/declaration/form/components/SupportingDocuments'
import { blankSupportingDocument } from 'features/declaration/models/blankItems'
import { HandleSubmitResult } from 'features/declaration/form/components/TransitFormTabHandle'
import CollapsibleFormGroup from '../../../../../../components/p5/CollapsibleFormGroup'
import useConsignmentItem from '../../../../consignment-item/components/useConsignmentItem'
import { toConsignmentItemFormValues } from '../../../../consignment-item/models/mapper'
import Packaging from '../../../../consignment-item/components/Packaging'
import TransportEquipment from '../../../../consignment-item/components/TransportEquipment'
import AdditionalReferences from '../../../../consignment-item/components/AdditionalReferences'
import EnsGoodsData from './EnsGoodsData'
import EnsPassiveBorderTransportMeans from './EnsPassiveBorderTransportMeans'
import useConsignmentItemForm, { ConsignmentItemFormSaveResult } from '../../../../consignment-item/components/useConsignmentItemForm'
import { useDeclarationEditorContext } from '../../../contexts/DeclarationEditorContext'
import LoadingOverlay from '../../../../../../components/p5/LoadingOverlay'
import useScopedServerRequirements from '../../../validations/useScopedServerRequirements'
import useNctsPhase from '../../../components/useNctsPhase'
import useDeclarationCountry from '../../../components/useDeclarationCountry'
import { notifyConsignmentItemSaveResult } from '../../../../consignment-item/components/notifyConsignmentItemSaveResult'
import applyFormFieldErrors, { FieldError } from '../../../../../../shared/rhf/applyFormFieldErrors'

export interface ConsignmentItemFormHandle {
  save: () => Promise<ConsignmentItemFormSaveResult>
  isDirty: () => boolean
}

interface ConsignmentItemFormProps {
  id: number
  houseConsignmentId: number
  onSaveSuccess?: () => void
  fieldErrors?: FieldError[]
}

const EnsConsignmentItemForm = forwardRef<ConsignmentItemFormHandle, ConsignmentItemFormProps>(({
  id,
  houseConsignmentId,
  onSaveSuccess,
  fieldErrors,
}, ref) => {
  const { t } = useTranslation()

  const { declarationData, editorData } = useDeclarationEditorContext()
  const { consignment, transitOperation } = declarationData
  if (!transitOperation?.id) throw new Error('Missing transit operation ID')
  if (!consignment?.id) throw new Error('Missing consignment ID')

  const { phase, isOptIn } = useNctsPhase()
  const country = useDeclarationCountry()
  const [isSaving, setIsSaving] = useState(false)
  const [isFormPopulated, setIsFormPopulated] = useState(false)
  const [isCleanBaselineSet, setIsCleanBaselineSet] = useState(false)

  const {
    consignmentItem, consignmentItemDocuments, additionalReferences, isLoading, saveConsignmentItemFormValues,
  } = useConsignmentItem({
    id,
    consignmentId: consignment.id,
    transitOperationId: transitOperation.id,
    houseConsignmentId,
  })

  const { form } = useConsignmentItemForm()
  const {
    reset, handleSubmit, formState: { isDirty },
  } = form

  const [passiveBorderTransportMeans, transportEquipment] = useWatch({
    control: form.control,
    name: ['passiveBorderTransportMeans', 'transportEquipment'],
  })
  const hasTransportData = (passiveBorderTransportMeans?.length ?? 0) > 0
    || (transportEquipment?.length ?? 0) > 0

  const { isLoading: isServerRequirementsLoading } = useScopedServerRequirements('consignmentItem', {
    transitOperationId: transitOperation.id,
    phase,
    isOptIn,
    houseConsignmentId,
    consignmentItemId: id,
  }, { enabled: isFormPopulated })

  useEffect(() => {
    if (isFormPopulated) return

    if (!consignmentItem || !consignmentItemDocuments || !additionalReferences) return
    reset(toConsignmentItemFormValues(true, consignmentItem, consignmentItemDocuments, additionalReferences))
    setIsFormPopulated(true)
  }, [reset, consignmentItem, consignmentItemDocuments, additionalReferences, isFormPopulated])

  useEffect(() => {
    if (!isFormPopulated || !fieldErrors) return
    applyFormFieldErrors(form, fieldErrors)
  }, [isFormPopulated, fieldErrors, form])

  useEffect(() => {
    if (isCleanBaselineSet || !isFormPopulated || isLoading || isServerRequirementsLoading) return
    reset(form.getValues(), { keepErrors: true })
    setIsCleanBaselineSet(true)
  }, [isCleanBaselineSet, isFormPopulated, isLoading, isServerRequirementsLoading, reset, form])

  const validateAndGet = () => new Promise<HandleSubmitResult<ConsignmentItemFormValues>>((resolve) => {
    handleSubmit(
      (data) => {
        resolve({ success: true, data })
      },
      (formErrors) => {
        resolve({ success: false, formErrors })
      },
    )()
  })

  const save = async (): Promise<ConsignmentItemFormSaveResult> => {
    setIsSaving(true)
    try {
      const result = await validateAndGet()
      if (!result.success) {
        // eslint-disable-next-line no-console
        console.error('consignment item form errors', result.formErrors)
        return { success: false, errorReason: 'validation' }
      }

      if (!result.data) {
        return { success: false, errorReason: 'general', errors: [{ message: 'Data missing' }] }
      }

      const { data: saved, errors } = await saveConsignmentItemFormValues({
        values: result.data,
        isDraft: true,
      })

      if (errors.length) {
        return { success: false, errorReason: 'general', errors }
      }

      form.reset(toConsignmentItemFormValues(
        true,
        saved.consignmentItem,
        saved.consignmentItemDocuments,
        saved.additionalReferences,
      ))

      onSaveSuccess?.()
      return { success: true, data: saved }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error saving consignment item', e)
      return { success: false, errorReason: 'unexpected', error: e }
    } finally {
      setIsSaving(false)
    }
  }

  useImperativeHandle(ref, () => ({
    save,
    isDirty: () => isDirty,
  }))

  const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    const result = await save()
    notifyConsignmentItemSaveResult(result, t)
  }

  return (
    <LoadingOverlay loading={isLoading || isServerRequirementsLoading || isSaving}>
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <div className="hc-form">
            <div className="row">
              <div className="col-lg-8 ci-goods-grid">
                <EnsGoodsData />
              </div>
              <div className="col-lg-4">
                <Packaging />
                <ConsignmentItemAdditionalSupplyChainActors />
              </div>
            </div>

            <CollapsibleFormGroup title={t('declaration.p5.consignmentItemDrawer.documents')}>
              <div className="row">
                <div className="col-lg-4">
                  <SupportingDocuments<ConsignmentItemFormValues>
                    scope="consignmentItem"
                    createDefault={() => blankSupportingDocument}
                    isEns
                    country={country}
                  />
                </div>
                <div className="col-lg-4">
                  <AdditionalReferences />
                </div>
              </div>
            </CollapsibleFormGroup>

            <CollapsibleFormGroup
              key={`transportation-${isFormPopulated}`}
              title={t('declaration.p5.consignmentItemDrawer.transportation')}
              initialOpen={hasTransportData}
            >
              <div className="row">
                <div className="col-12">
                  <EnsPassiveBorderTransportMeans
                    borderModeOfTransport={editorData?.borderModeOfTransport ?? ''}
                    entriesGrid
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <TransportEquipment />
                </div>
              </div>
            </CollapsibleFormGroup>
          </div>
        </form>
      </FormProvider>
    </LoadingOverlay>
  )
})

EnsConsignmentItemForm.defaultProps = {
  onSaveSuccess: undefined,
  fieldErrors: undefined,
}

export default EnsConsignmentItemForm
